/**
 * Base implementation of authentication service
 */
export class AuthBase {
    clientId;
    clientSecret;
    region;
    grantType;
    scope;
    ssoUrl;
    cachedToken = null;
    tokenExpiry = 0;
    fetchImpl;
    /**
     * Create a new authentication service
     * @param config - Configuration options
     * @param fetchImpl - The fetch implementation to use
     */
    constructor(config, fetchImpl) {
        this.clientId = config.clientId;
        this.clientSecret = config.clientSecret;
        this.region = config.region;
        this.grantType = config.grantType;
        this.scope = config.scope;
        this.ssoUrl = config.ssoUrl;
        this.fetchImpl = fetchImpl;
    }
    /**
     * Authenticate with username and password
     * @param username - The username
     * @param password - The password
     * @returns Promise with the authentication response
     */
    async authenticate(username, password) {
        const options = {
            method: "POST",
            mode: "cors",
            headers: {
                "Content-Type": "application/x-www-form-urlencoded",
            },
            body: new URLSearchParams({
                client_id: this.clientId,
                client_secret: this.clientSecret,
                region: this.region,
                grant_type: this.grantType,
                username: username,
                password: password,
                scope: this.scope,
            }),
        };
        try {
            const response = await this.fetchImpl(`${this.ssoUrl}/connect/token`, options);
            const data = await response.json();
            if (data && data.access_token) {
                this.cachedToken = data.access_token;
                this.tokenExpiry = Date.now() + data.expires_in * 1000 - 60000; // Subtract 1 minute buffer
            }
            return data;
        }
        catch (error) {
            console.error("Error:", error);
            return null;
        }
    }
    /**
     * Get the user ID from the token payload
     * @returns The user ID or null if not available
     */
    getUserId() {
        return this.getPayloadField("id");
    }
    /**
     * Get the username from the token payload
     * @returns The username or null if not available
     */
    getUsername() {
        return this.getPayloadField("name");
    }
    /**
     * Get a field from the token payload
     * @param key - The field key
     * @returns The field value
     * @internal This method is for internal use only
     */
    getPayloadField(key) {
        const token = this.getRawToken();
        const decoded = this.decodeToken(token);
        if (!decoded)
            return null;
        return decoded[key];
    }
    /**
     * Decode a JWT token
     * @param token - The token to decode
     * @returns The decoded token payload
     */
    decodeToken(token) {
        if (!token)
            return null;
        try {
            // Simple JWT decoding (payload is the second part)
            const base64Url = token.split(".")[1];
            const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
            const jsonPayload = decodeURIComponent(atob(base64)
                .split("")
                .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
                .join(""));
            return JSON.parse(jsonPayload);
        }
        catch (error) {
            console.error("Error decoding token:", error);
            return null;
        }
    }
}
